'use client'

/**
 * The application's one dialog (§31, §46).
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 *
 * `ConfirmDialog` was a good dialog in the wrong place. It lived in
 * `admin/robot/`, which is where it was first needed, and by the time six
 * components wanted it one of them was a dashboard page importing
 * `../../admin/robot/ConfirmDialog` — a public-facing account screen reaching
 * into the robot console for a question box. A component every part of the
 * product asks for is not a feature of one console.
 *
 * It also could not be asked for from anywhere. Every caller had to hold an
 * `asking` boolean, render the dialog as a sibling of its own button, and thread
 * `onConfirm`/`onCancel` back out again. That is six copies of one state machine,
 * and the copies had already drifted: some closed on success only, some closed
 * before the work finished, and one left the dialog up if the action threw.
 *
 * So: one dialog, mounted once in the root layout, asked for as a promise.
 *
 *     const dialog = useDialog()
 *     if (await dialog.confirm({ … })) …
 *
 * ── Waiting is the same event as asking ─────────────────────────────────────
 *
 * The second half of this file is the part that was missing entirely. Before it,
 * confirming a slow action closed the dialog immediately and left the reader
 * looking at an unchanged page with a 14px spinner on the button they had just
 * dismissed. Whether anything was happening was a guess.
 *
 * A dialog that asked the question is already the thing holding the reader's
 * attention, so it is the right thing to keep holding it: `run` keeps it open,
 * busy, until the work settles. Escape and the backdrop stop closing it while
 * that is true — dismissing the window does not cancel the request behind it, so
 * offering to would be a lie — and a failure keeps it open with the reason on it
 * rather than vanishing and leaving a toast to explain.
 *
 * ── It carries its own surface ──────────────────────────────────────────────
 *
 * `.confirm` was styled from `--glass-border` and `--radius-panel`, which are
 * declared on `.admin` and `.dash` and deliberately not on `:root` — the public
 * site is INDUSTRIAL GOLD and square by rule, and hoisting a 14px radius to
 * where every page can see it would soften the brand to restyle two shells.
 * A dialog portalled to `<body>` is outside both scopes, so those tokens would
 * resolve to nothing and the box would have had no border at all on the search
 * page. `.dlg` in globals.css therefore declares everything it needs.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

/**
 * What an operation says back — the same shape the server actions already
 * return, so `run` can be handed one directly with nothing in between.
 */
export type DialogResult = { ok: boolean; message: string }

const isResult = (v: unknown): v is DialogResult =>
  typeof v === 'object' && v !== null && 'ok' in v && typeof (v as DialogResult).ok === 'boolean'

export type ConfirmSpec = {
  title: string
  /** What will actually happen, in the reader's terms. Never "Are you sure?". */
  body: string
  confirmLabel: string
  /** Defaults to "Keep it" for a danger ask and "Cancel" otherwise. */
  cancelLabel?: string
  /** `danger` reserves the red confirm button for what it is actually for. */
  tone?: 'danger' | 'default'
  /**
   * Make them type something before the confirm button works (§64.4).
   *
   * For the handful of actions with no single name to put in front of somebody —
   * "delete all 1,284 entities" — where a second OK is not a second decision.
   * Reading a number off the screen and typing it is a different act from
   * clicking through twice, and it is the difference between an operator who
   * meant it and one who was clearing a filter.
   *
   * Matched after trimming, and exactly otherwise: a challenge that accepts a
   * near-miss is decoration.
   */
  challenge?: { label: string; answer: string }
  /**
   * Held open and busy until this settles.
   *
   * Return a `DialogResult` and a failure keeps the dialog up with the message
   * on it; return anything else (or nothing) and settling closes it. A thrown
   * error is caught and shown the same way a failed result is — a dialog that
   * disappears on an exception is how an action silently does nothing.
   */
  run?: () => Promise<unknown>
  /** What to say while `run` is in flight. Defaults to "Working…". */
  busyLabel?: string
}

export type WaitSpec = {
  title: string
  body?: string
  busyLabel?: string
}

export type DialogApi = {
  /**
   * Ask, and wait for the answer.
   *
   * Resolves `true` when the reader confirmed — and, if `run` was given, when
   * that work also completed. Escape, the backdrop, the cancel button and a
   * failed `run` all resolve `false`.
   */
  confirm(spec: ConfirmSpec): Promise<boolean>
  /**
   * No question — just hold the reader while something they have to wait for
   * runs. Resolves with whatever the task returned; rethrows what it threw,
   * after showing it, so a caller can still branch on failure.
   */
  wait<T>(spec: WaitSpec, task: () => Promise<T>): Promise<T>
}

/**
 * How long an operation may take before the reader is told to wait.
 *
 * Below this, saying nothing is the better answer — the action simply happens.
 * A quarter of a second is roughly where a delay stops reading as "instant" and
 * starts reading as "did that work?", and it is short enough that anything
 * genuinely slow still announces itself well before the reader clicks again.
 */
const BUSY_AFTER_MS = 250

const DialogContext = createContext<DialogApi | null>(null)

/**
 * Ask for the dialog.
 *
 * Throws when the provider is missing rather than returning a no-op API. A
 * confirmation that silently does not appear is worse than a crash: the caller
 * proceeds as though the reader agreed to something they were never shown.
 */
export function useDialog(): DialogApi {
  const api = useContext(DialogContext)
  if (!api) throw new Error('useDialog must be used inside <DialogProvider> (see app/layout.tsx)')
  return api
}

/** What the one dialog is currently doing. `null` is closed. */
type State =
  | { kind: 'ask'; spec: ConfirmSpec; settle: (ok: boolean) => void }
  | { kind: 'busy'; title: string; body?: string; busyLabel: string }
  | { kind: 'failed'; title: string; message: string; settle: () => void }
  | null

export function DialogProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<State>(null)
  /* What has been typed into a `challenge`, if the open ask has one. Held here
     rather than in the spec so it is cleared by the same transition that opens
     the next question — a stale answer must never carry over to another ask. */
  const [typed, setTyped] = useState('')
  const ref = useRef<HTMLDialogElement>(null)

  // `document` does not exist while this renders on the server and the portal
  // needs it. Tracked rather than assumed so the first client render matches the
  // server's empty one and hydration stays quiet — same reason as `CtlNote`.
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  /*
   * Open and close the native element to match the state.
   *
   * `showModal()` is what buys the focus trap, the inert background, Escape, and
   * a backdrop in the top layer — none of which is worth reimplementing, and all
   * of which is easy to get subtly wrong by hand. The element is kept mounted
   * and toggled rather than mounted per-question, because closing it is how the
   * browser returns focus to whatever opened it.
   */
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (state && !el.open) el.showModal()
    if (!state && el.open) el.close()
  }, [state])

  /*
   * Nothing in this API is allowed to leave the dialog open forever.
   *
   * `run` is wrapped so that every exit — resolved, rejected, or a falsy result
   * — passes through one place. The earlier per-caller version is exactly where
   * this went wrong: `start(async () => { setResult(await fn()); setAsking(false) })`
   * never reaches `setAsking(false)` if `fn` throws.
   *
   * ── `immediate`, and why waiting to say "wait" is the point ────────────────
   *
   * Most of these actions answer in well under a second. Throwing a modal up for
   * 120ms and tearing it down again is worse than showing nothing: the reader
   * gets a flash they cannot read, the page behind it goes inert and back, and
   * the eye is pulled to a window that has already gone. So `wait` holds the
   * busy state back by `BUSY_AFTER_MS` and simply never shows it if the work
   * finishes first — which is the common case, and is why a fast action can be
   * routed through here without anybody seeing a dialog at all.
   *
   * `confirm`'s `run` passes `immediate`, because there is no flash to avoid: a
   * dialog is already open and being looked at, and delaying the switch would
   * leave the reader on a live question whose buttons no longer do anything.
   */
  const perform = useCallback(
    async (
      title: string,
      body: string | undefined,
      busyLabel: string,
      task: () => Promise<unknown>,
      immediate: boolean,
    ): Promise<{ ok: boolean; value?: unknown; error?: unknown }> => {
      const show = () => setState({ kind: 'busy', title, ...(body ? { body } : {}), busyLabel })
      let timer: ReturnType<typeof setTimeout> | null = null
      if (immediate) show()
      else timer = setTimeout(show, BUSY_AFTER_MS)

      /* Cleared on every exit, including the thrown one. A pending timer that
         fires after the work is done opens a dialog nothing will ever close. */
      const settled = () => {
        if (timer) clearTimeout(timer)
        timer = null
      }

      try {
        const value = await task()
        settled()
        if (isResult(value) && !value.ok) {
          // Hold it open with the reason. The reader is still looking here.
          await new Promise<void>((resolve) =>
            setState({ kind: 'failed', title, message: value.message, settle: resolve }),
          )
          return { ok: false, value }
        }
        setState(null)
        return { ok: true, value }
      } catch (error) {
        settled()
        const message =
          error instanceof Error && error.message ? error.message : 'That did not go through.'
        await new Promise<void>((resolve) =>
          setState({ kind: 'failed', title, message, settle: resolve }),
        )
        return { ok: false, error }
      }
    },
    [],
  )

  const api = useMemo<DialogApi>(
    () => ({
      confirm: (spec) =>
        new Promise<boolean>((resolve) => {
          setTyped('')
          setState({
            kind: 'ask',
            spec,
            settle: async (ok) => {
              if (!ok || !spec.run) {
                setState(null)
                resolve(ok)
                return
              }
              const outcome = await perform(
                spec.title,
                undefined,
                spec.busyLabel ?? 'Working…',
                spec.run,
                true,
              )
              resolve(outcome.ok)
            },
          })
        }),

      wait: async (spec, task) => {
        const outcome = await perform(
          spec.title,
          spec.body,
          spec.busyLabel ?? 'Working…',
          task as () => Promise<unknown>,
          false,
        )
        // Rethrown AFTER the reader has seen it, so a caller can still branch on
        // failure without having to render the failure itself.
        if (outcome.error !== undefined) throw outcome.error
        return outcome.value as never
      },
    }),
    [perform],
  )

  return (
    <DialogContext.Provider value={api}>
      {children}
      {mounted &&
        createPortal(
          <dialog
            className={`dlg${state?.kind === 'ask' && state.spec.tone === 'danger' ? ' dlg-danger' : ''}`}
            ref={ref}
            aria-busy={state?.kind === 'busy'}
            /*
             * Fires for Escape and for `close()`. While something is running it
             * is refused: dismissing this window would not cancel the request
             * behind it, so letting it close would tell the reader the operation
             * had stopped when it had not.
             */
            onCancel={(e) => {
              if (state?.kind === 'busy') {
                e.preventDefault()
                return
              }
              if (state?.kind === 'ask') state.settle(false)
              if (state?.kind === 'failed') state.settle()
            }}
            // The backdrop is the element itself; a click on any child is not on
            // it. Same rule as Escape, and refused for the same reason.
            onClick={(e) => {
              if (e.target !== ref.current || state?.kind === 'busy') return
              if (state?.kind === 'ask') state.settle(false)
              if (state?.kind === 'failed') state.settle()
            }}
          >
            {state?.kind === 'ask' && (
              <>
                <h2>{state.spec.title}</h2>
                <p>{state.spec.body}</p>

                {state.spec.challenge && (
                  <label className="dlg-challenge">
                    <span>{state.spec.challenge.label}</span>
                    <input
                      type="text"
                      value={typed}
                      onChange={(e) => setTyped(e.target.value)}
                      // Enter is the same as pressing the confirm button, and is
                      // refused on the same condition — so a held Enter key
                      // cannot carry somebody through an unanswered challenge.
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter') return
                        e.preventDefault()
                        if (typed.trim() === state.spec.challenge?.answer) state.settle(true)
                      }}
                      autoComplete="off"
                      spellCheck={false}
                      /* Focus goes to the field when there is one: it is the
                         next thing to do, and the cancel button is still one
                         Shift+Tab or one Escape away. */
                      autoFocus
                    />
                  </label>
                )}

                <div className="dlg-actions">
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => state.settle(false)}
                    autoFocus={!state.spec.challenge}
                  >
                    {state.spec.cancelLabel ??
                      (state.spec.tone === 'danger' ? 'Keep it' : 'Cancel')}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm${state.spec.tone === 'danger' ? ' btn-danger' : ' btn-primary'}`}
                    disabled={
                      state.spec.challenge ? typed.trim() !== state.spec.challenge.answer : false
                    }
                    onClick={() => state.settle(true)}
                  >
                    {state.spec.confirmLabel}
                  </button>
                </div>
              </>
            )}

            {state?.kind === 'busy' && (
              <>
                <h2>{state.title}</h2>
                {state.body && <p>{state.body}</p>}
                {/* `role="status"` so a screen reader is told the wait began
                    without the focus being moved off whatever it was on. */}
                <p className="dlg-busy" role="status">
                  <span className="dlg-spinner" aria-hidden="true" />
                  {state.busyLabel}
                </p>
              </>
            )}

            {state?.kind === 'failed' && (
              <>
                <h2>{state.title}</h2>
                {/* `alert`, unlike the wait above: this one has to interrupt. */}
                <p className="dlg-failed" role="alert">
                  {state.message}
                </p>
                <div className="dlg-actions">
                  <button type="button" className="btn btn-sm" onClick={state.settle} autoFocus>
                    Close
                  </button>
                </div>
              </>
            )}
          </dialog>,
          document.body,
        )}
    </DialogContext.Provider>
  )
}
